import React, { useEffect, useMemo } from "react";
import styled from "styled-components";
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import {
    Box,
    Breadcrumbs as MuiBreadcrumbs,
    Button,
    Checkbox,
    Chip as MuiChip,
    Divider as MuiDivider,
    Grid,
    IconButton,
    Link,
    Paper as MuiPaper,
    
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Toolbar,
    Tooltip,
    Typography,
} from "@mui/material";

import { green, grey, orange, red } from "@mui/material/colors";

import {
    Add as AddIcon,
    Archive as ArchiveIcon,
    FilterList as FilterListIcon,
    RemoveRedEye as RemoveRedEyeIcon,
    Edit as EditIcon,
    Delete as DeleteIcon
    } from "@mui/icons-material";

import { spacing } from "@mui/system";
import { IconGenerator } from "./mini_components";
import { isNumber } from "lodash";
import { Translate } from "react-localize-redux";
import { getVisibleRows } from "./enhancedTableSort";

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Paper = styled(MuiPaper)(spacing);

const TableSizes = styled(Table)`
    ${({ miniTable }) => !miniTable && `
        min-width:600px
    `}
    `;
const Chip = styled(MuiChip)`
${spacing};

background: ${(props) => props.shipped && green[500]};
background: ${(props) => props.processing && orange[700]};
background: ${(props) => props.cancelled && red[500]};
color: ${(props) => props.theme.palette.common.white};
`;

const Spacer = styled.div`
flex: 1 1 100%;
`;

const ToolbarTitle = styled.div`
min-width: 150px;
`;

const StyledTableCell = styled(TableCell)`
  &:last-child {
    position: sticky;
    right: 0;
    background: white;
    z-index: 2;
  }
`;

function EnhancedTableHead(props) {
    const {
        onSelectAllClick,
        order,
        orderBy,
        disableOrder,
        numSelected,
        rowCount,
        onRequestSort, 
        headCells
    } = props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };
    const [checkboxesState, setCheckboxesState] = React.useState({});

    function changeCheckboxState(headId, callback){
        setCheckboxesState({ ...checkboxesState, [headId]: !checkboxesState[headId] })
        callback();
    }
    return (
        <TableHead style={{fontWeight:'600', backgroundColor:grey[100]}}>
        <TableRow> 
            {!props.noSelectable &&
                <TableCell padding="checkbox">
                    <Checkbox
                        indeterminate={numSelected > 0 && numSelected < rowCount}
                        checked={rowCount > 0 && numSelected === rowCount}
                        onChange={onSelectAllClick}
                        inputProps={{ "aria-label": "select all" }}
                    />
                </TableCell>
            }
            {headCells.map((headCell) => {
                const disableOrderCell = props.disableOrder || (props.typeHeadCells[headCell.id] === 'object' && !props.typeHeadCells[headCell.id+"Name"] );
                return(
                    <TableCell
                        key={headCell.id}
                        align={headCell.alignment}
                        padding={headCell.disablePadding || props.dense ? "none" : "default"}
                        sortDirection={orderBy === headCell.id ? order : false}
                        style={headCell.width ? { width: headCell.width } : undefined}
                    >
                        <TableSortLabel
                            active={disableOrderCell ? false : orderBy === headCell.id }
                            hideSortIcon={disableOrderCell}
                            direction={disableOrderCell ? "" : orderBy === headCell.id ? order : "asc"}
                            onClick={!disableOrderCell ? createSortHandler(headCell.id) : null}
                            style={{fontWeight:"600"}}
                        >
                        {
                            headCell.markAllCallback &&
                            <span><Checkbox
                            
                                checked={checkboxesState[headCell.id] || false}
                                onChange={() => changeCheckboxState(headCell.id, headCell.markAllCallback)}
                                inputProps={{ "aria-label": "select all" }}
                            /></span>
                        }
                        {headCell.label}
                        </TableSortLabel>
                    </TableCell>
                )
            }
            
            )}
            {
                (props.actions && props.actions.length > 0 || props.renderExtraActions) &&
                <TableCell style={{fontWeight:"600"}} align="center"><Translate id="general.actions"/></TableCell>
            } 
        </TableRow>
        </TableHead>
    );
}

let EnhancedTableToolbar = (props) => {
const { numSelected, noSelectable } = props;

return (
    <Toolbar>
    <ToolbarTitle style={{width: '100%'}}>
        {numSelected > 0 ? (
        <Typography color="inherit" variant="subtitle1">
            {numSelected} selected
        </Typography>
        ) : (
        <Typography variant="h5" id="tableTitle"  style={{fontSize:'1.2rem'}}>
            {props.title}
        </Typography>
        )}
    </ToolbarTitle>
    <Spacer />
    <div>
        {numSelected > 0 ? (
        <Tooltip title="Delete">
            <IconButton aria-label="Delete" size="large">
            <ArchiveIcon />
            </IconButton>


            
        </Tooltip>
        ) : noSelectable ? (
        <Tooltip title="Filter list">
            <IconButton aria-label="Filter list" size="large">
            <FilterListIcon />
            </IconButton>
        </Tooltip>
        ) : null }
    </div>
    </Toolbar>
);
};

export function EnhancedTable(props) {
    const [order, setOrder] = React.useState( props.order ? props.order.orderBy : "asc");
    const [orderBy, setOrderBy] = React.useState(props.order ? props.order.property : "customer");
    const [selected, setSelected] = React.useState([]);
    const [page, setPage] = React.useState(0);
    // SHER-474: in server-pagination mode the page size is controlled by the parent
    // (it drives the limit sent to the API); fall back to 10 as before otherwise.
    const [rowsPerPage, setRowsPerPage] = React.useState( props.noFooter ? props.rows.length : (props.rowsPerPage ?? 10));
    const {rows, headCells, actions, titleTable, noSelectable} = props;

    useEffect(() => {
        if(isNumber(props.currentPage)){
            setPage(props.currentPage);
        }
    }, [props.currentPage])

    const typeHeadCells = rows.length > 0 ? headCells.reduce((accumulator, headCell) => {
        let valueType = rows[0][headCell.id] ? typeof rows[0][headCell.id] : null;
        if(valueType === "object"){
            const alternativeHeadCell = rows[0][headCell.id+"Name"];
            valueType = alternativeHeadCell ? typeof alternativeHeadCell : null;
        }
        accumulator[headCell.id] = valueType;
        return accumulator
        }, {}) : {};

    const handleRequestSort = (event, property) => {
        const isAsc = (orderBy === property || orderBy === property+"Name") && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        if(typeof rows[0][property] === "object"){
            setOrderBy(property+"Name");
        }
        else{
            setOrderBy(property);
        }
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
        const newSelecteds = rows.map((n) => n.id);
        setSelected(newSelecteds);
        return;
        }
        setSelected([]);
    };

    useEffect(() => {
        if(props.noPagination || props.noFooter){
            setRowsPerPage(props.rows.length + 1);
        }
    }, [props.rows])

const handleClick = (event, id) => {
    console.log("Pincho en la tabla"+id, event );
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
    newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
    newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
    newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
    newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
    );
    }

    setSelected(newSelected);
};

const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if(typeof props.changePageCallback === 'function'){
        props.changePageCallback(newPage)
    }
    // SHER-474: in server mode the page rows live on the server — tell the parent so it
    // can refetch the new window. Client mode is unchanged (it slices locally).
    if(props.serverPagination && typeof props.onServerPageChange === 'function'){
        props.onServerPageChange(newPage, rowsPerPage);
    }
};

const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    // SHER-474: a new page size resets to page 0 and refetches in server mode.
    if(props.serverPagination && typeof props.onServerPageChange === 'function'){
        props.onServerPageChange(0, newRowsPerPage);
    }
};


const isSelected = (id) => selected.indexOf(id) !== -1;

// SHER-474: in server mode `rows` IS the current page already (the parent fetched
// just that window), so don't slice/sort it locally — render it as given. Client
// mode keeps the existing sort-then-slice behaviour.
const visibleRows = useMemo(
    () => props.serverPagination ? rows : getVisibleRows(rows, order, orderBy, page, rowsPerPage),
    [rows, order, orderBy, page, rowsPerPage, props.serverPagination]
);

const emptyRows = rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

    function renderActions(row){
        let buttonActions = []
        for(let i = 0; i < props.actions.length; i++){
            const action = props.actions[i];
            const func = action["func"];
            const check = action["check"];
            if(check === undefined || check(row)){
                const iconButton = (
                    <IconButton
                        key={i}
                        data-testid={action.type}
                        aria-label={action.type}
                        onClick={(e) => {
                            e.stopPropagation();
                            func(row.id);
                            
                        }}
                        size="medium">
                        <IconGenerator type={typeof action.type  === 'function' ? action.type(row.id) : action.type} size="medium" color={action.color ? action.color(row.id) : undefined} />
                    </IconButton>
                );

                // Wrap with Tooltip if action has a tooltip property
                if (action.tooltip) {
                    const tooltipContent = typeof action.tooltip === 'function' ? action.tooltip(row) : action.tooltip;
                    buttonActions.push(
                        <Tooltip key={i} title={tooltipContent} arrow>
                            {iconButton}
                        </Tooltip>
                    );
                } else {
                    buttonActions.push(iconButton);
                }
            }
            
        }
        if (props.renderExtraActions) {
            const extra = props.renderExtraActions(row);
            if (extra) {
                buttonActions.push(<React.Fragment key="extra-actions">{extra}</React.Fragment>);
            }
        }
        return buttonActions;
        
    }
function renderTableRow(isItemSelected, index, labelId, row, draggableProps, dragHandleProps, innerRef){
    const hasColsPan = row.hasOwnProperty("colSpan");
    let headCellColSpanPrev = null;
    let headCellColSpan = null;
    if(hasColsPan){
        //find the headCell previous to the one that should have a colSpan
        const headCellColSpanIndex = headCells.findIndex(headCell => headCell.id === row.colSpan);
        headCellColSpan = headCells[headCellColSpanIndex];
        headCellColSpanPrev = headCells[headCellColSpanIndex - 1];
    }
    return(
        <TableRow
            hover
            role="checkbox"  
            aria-checked={isItemSelected}
            tabIndex={-1}
            key={`${index}`}
            selected={isItemSelected}
            onClick={props.selectRow ? () => props.selectRow( row.id) : null}
            {...draggableProps}
            {...dragHandleProps}
            ref={ innerRef }
        >   
        {
            !noSelectable &&
            <TableCell padding="checkbox">
                <Checkbox
                    checked={isItemSelected}
                    inputProps={{ "aria-labelledby": labelId }}
                    onClick={(event) => handleClick(event, row.id)}
                />
            </TableCell>
        }
            
        {
            headCells.map(headCell =>{
                let value = row[headCell.id];
                if(typeof row[headCell.id] === "boolean"){ 
                    value = <Checkbox checked={row[headCell.id]} onClick={props.callBackCheckbox ? () => props.callBackCheckbox(row.id, headCell.id, !row[headCell.id]) : null} />
                }
                if(headCellColSpanPrev && headCellColSpanPrev.id === headCell.id){
                    return null;
                }
                return <TableCell key={headCell.id}
                            colSpan={headCellColSpan && headCellColSpan.id === headCell.id ? 2 : 1}
                            padding={props.dense ? "none" : "default"}
                            align={headCellColSpan && headCellColSpan.id === headCell.id ? "right" : headCell.alignment}
                            style={headCell.width ? { width: headCell.width } : undefined}>{value}</TableCell>
                
            })
        }         
        {
            (props.actions && props.actions.length > 0 || props.renderExtraActions) && 
            <TableCell padding="none" align="center">
                <Box display="flex" justifyContent="center">
                    {
                        renderActions(row)
                    }
                </Box>
            </TableCell>
        }   
            </TableRow>
    )
}
function renderBody(){
    return props.droppableId ? (
        <Droppable droppableId={props.droppableId ? props.droppableId : "droppableId"}>
        {
            (provided, snapshot) =>(
                
                <TableBody {...provided.draggableProps}
                    ref={provided.innerRef}>
                    
                {visibleRows.map((row, index) => {
                    const isItemSelected = isSelected(row.id);
                    const labelId = `enhanced-table-checkbox-${index}`;

                    return props.droppableId ? (
                        <Draggable key={row.id} draggableId={`item-${props.droppableId}-${row.id}`} 
                            index={index}
                            isDragDisabled={!props.droppableId}>
                            {
                                (provided, snapshot)=>(
                                    
                                    renderTableRow(isItemSelected, index, labelId, row, provided.draggableProps, provided.dragHandleProps, provided.innerRef)
                                )
                            }
                            
                        </Draggable>
                    ) : 
                    renderTableRow(isItemSelected, index, labelId, row, {}, {}, null)
                    })
                }
                { provided.placeholder}
                </TableBody>
                
            )
        }
    </Droppable>) : 
    
    <TableBody>
        {visibleRows.map((row, index) => {
            const isItemSelected = isSelected(row.id);
            const labelId = `enhanced-table-checkbox-${index}`;

            return renderTableRow(isItemSelected, index, labelId, row, {}, {}, null)
            
            })
        }
    </TableBody>;
}

return (
    <div >
    <Paper>
        {
            !props.noHeader &&
            <EnhancedTableToolbar title={titleTable} numSelected={selected.length} />
        }
        <TableContainer>
            <TableSizes miniTable={props.miniTable} 
                aria-labelledby="tableTitle"
                size={"medium"} 
                aria-label="enhanced table"
            >
                <EnhancedTableHead
                    numSelected={selected.length}
                    order={order}
                    dense={props.dense}
                    disableOrder={props.disableOrder}
                    orderBy={orderBy}
                    onSelectAllClick={handleSelectAllClick}
                    onRequestSort={handleRequestSort}
                    rowCount={rows.length}
                    typeHeadCells={typeHeadCells}
                    headCells={headCells}
                    actions={actions}
                    noSelectable={noSelectable}
                />
                {
                    renderBody()
                }
                
            </TableSizes>
        </TableContainer>
        {
            !props.noFooter &&
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                // SHER-474: server mode sizes the pager from the server-side total so the
                // footer reads e.g. "1–10 of 420" even though only 10 rows are loaded.
                count={props.serverPagination ? (typeof props.totalCount === 'number' ? props.totalCount : rows.length) : rows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                />
        }
        
    </Paper>
    </div>
);
}